import { useState, useEffect, useCallback, useRef } from 'react';
import type { Vehicle } from '../types/vehicle';
import { getAllVehicles, getSubunitNames, getAllDevices } from '../lib/api/endpoints';
import { vehicleFromDto, applyVehicleLiveUpdate } from '../lib/api/mappers';
import { subscribeVehiclesLive } from '../lib/firestore/vehiclesLive';
import { useAuth } from '../context/AuthContext';

// FE-04: antes leía MOCK_VEHICLES y simulaba movimiento con
// setInterval+Math.random(). Ahora trae el snapshot real de GET /vehicles.
// FE-03: el movimiento en vivo se monta sobre esta misma lista — se
// suscribe a `vehicles-live` (Firestore, ADR-008) y solo actualiza
// posición/estado por `vehicleId`; plate/model/driver siguen viniendo de
// REST, que el documento vivo no trae.
// `refetch` (bugfix admin-users-crud, 2026-09-01) permite recargar tras un
// alta/edición real desde el panel de administración.
export function useVehicles() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // issue #68/#112bis: un solo GET /devices para toda la lista, unido en
      // memoria por `installedVehicleId` — evita el N+1 de pedirlo vehículo
      // por vehículo, y es el mismo patrón que ya usa `subUnitName`.
      //
      // 🚨 issue #167: el IMEI es un dato OPCIONAL y NO puede viajar en el
      // mismo `Promise.all` que los vehículos. `GET /devices` exige
      // `ActionManageDevices` = {Administrador, Supervisor} (`console/scope.go`),
      // así que al Operador y al Ejecutivo les responde 403: el `Promise.all`
      // rechazaba entero, el `catch` de abajo hacía `setVehicles([])` y las ~11
      // pantallas que usan este hook se quedaban VACÍAS — el SOS del Operador
      // decía "Sin vehículo asignado" con su asignación vigente en la base.
      // El `catch` local degrada el IMEI a "no lo sé" en vez de tumbar la lista.
      const [items, subunitNames, devices] = await Promise.all([
        getAllVehicles(),
        getSubunitNames(),
        getAllDevices().catch(() => [] as Awaited<ReturnType<typeof getAllDevices>>),
      ]);
      if (cancelledRef.current) return;
      const imeiByVehicleId = new Map(devices.filter(d => d.installedVehicleId).map(d => [d.installedVehicleId as string, d.imei]));
      setVehicles(items.map(dto => vehicleFromDto(dto, subunitNames.get(dto.subunitId ?? '') ?? '', imeiByVehicleId.get(dto.id) ?? '')));
      setError(null);
    } catch (err) {
      // Sin sesión real de Firebase (p. ej. loginDev(), FE-01) el cliente
      // HTTP lanza antes de llegar a la red — se trata igual que un error
      // de red: lista vacía, no una pantalla rota.
      if (!cancelledRef.current) {
        setVehicles([]);
        setError(err instanceof Error ? err : new Error('Error desconocido cargando vehículos'));
      }
    } finally {
      if (!cancelledRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    return () => { cancelledRef.current = true; };
  }, [load]);

  // FE-03: sin `firestoreCollections` (loginDev, FE-01, nunca llama a
  // GET /me) no hay vivo disponible — se degrada a la foto fija de REST,
  // igual que un error de red. El filtro por sub-unidad lo aplica
  // subscribeVehiclesLive con la misma lista que ya resolvió AuthContext.
  useEffect(() => {
    const collectionPath = user?.firestoreCollections?.vehicles;
    const subunitIds = user?.subunitIds;
    if (!collectionPath || !subunitIds) return;

    const unsubscribe = subscribeVehiclesLive(
      collectionPath,
      subunitIds,
      liveDocs => {
        if (cancelledRef.current) return;
        const liveById = new Map(liveDocs.map(doc => [doc.vehicleId, doc]));
        setVehicles(prev => prev.map(v => {
          const live = liveById.get(v.id);
          return live ? applyVehicleLiveUpdate(v, live) : v;
        }));
      },
      err => {
        // Igual que un error de REST: no rompe la pantalla, se queda con la
        // última foto conocida en vez de vaciar el mapa.
        if (!cancelledRef.current) setError(err);
      },
    );

    return () => unsubscribe();
  }, [user?.firestoreCollections?.vehicles, user?.subunitIds]);

  const selectedVehicle = vehicles.find(v => v.id === selectedId) ?? null;

  const selectVehicle = useCallback((id: string | null) => setSelectedId(id), []);

  return { vehicles, isLoading, error, selectedVehicle, selectVehicle, refetch: load };
}
